/**
 * NotificationBell — header dropdown for the in-app notification feed: both
 * human-collaboration kinds (docs/COLLABORATION_CONTRACT.md §3:
 * `lead_assigned` + org-invite/share/role events) AND AI virtual-staff kinds
 * (`mas_persona_action`, `staff_decision_needed`, `staff_escalation`,
 * `staff_delivery` — an agent acted, needs arbitration, breached its SLA, or
 * finished a deliverable). Mounted additively in DashboardLayout's header
 * controls, next to the theme picker.
 *
 * Shows an unread badge, a Tout/Équipe IA/Collaboration segmented filter, a
 * recent list (high-priority AI-staff rows — decisions/escalations — stand
 * out with an accent border + a "Priorité" tag), per-row mark-read on click
 * with a kind-aware deep-link, "Tout marquer lu", and a gear button opening
 * the NotificationPrefs panel (which kinds are allowed to surface at all —
 * client-side, localStorage). Degrades to a quiet empty state instead of an
 * error when the feed endpoint isn't deployed yet (404/501/503 — see
 * lib/api/notifications.ts).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { kindInfo, notificationGroup, type NotificationGroup } from '@/lib/api/notifications';
import { targetFor } from './notificationTarget';
import { timeAgo } from '@/routes/crm/format';
import { NotificationPrefs } from './NotificationPrefs';
import { useNotifications } from './useNotifications';
import { confirmAsync } from '@/ui';
import { useT, tr } from '@/i18n';

const BellIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="h-[18px] w-[18px]"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a2 2 0 0 0 3.4 0" />
  </svg>
);

const GearIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 2.5v1.6M10 15.9v1.6M17.5 10h-1.6M4.1 10H2.5M15.13 4.87l-1.13 1.13M6 14l-1.13 1.13M15.13 15.13 14 14M6 6 4.87 4.87" />
  </svg>
);

const TrashIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 20 20"
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 5.5h12M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5M6 5.5l.6 10a1 1 0 0 0 1 .9h4.8a1 1 0 0 0 1-.9l.6-10M8.5 9v4.5M11.5 9v4.5" />
  </svg>
);

type GroupFilter = 'all' | NotificationGroup;

const GROUP_FILTER_LABEL: Record<GroupFilter, string> = {
  all: tr('routes.notifications.notificationBell.all'),
  ai_staff: tr('routes.notifications.notificationBell.aiTeam'),
  collaboration: tr('routes.notifications.notificationBell.collaboration'),
  publishing: tr('routes.notifications.notificationBell.publishing'),
};

export function NotificationBell() {
  const { t: tx } = useT();
  const { items, unreadCount, loading, error, markRead, markAllRead, remove, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const filteredItems = useMemo(
    () => (groupFilter === 'all' ? items : items.filter((n) => notificationGroup(n) === groupFilter)),
    [items, groupFilter],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  const onClearAll = async () => {
    const ok = await confirmAsync({
      title: tx('routes.notifications.notificationBell.clearAllTitle'),
      message: tx('routes.notifications.notificationBell.clearAllMessage'),
      confirmLabel: tx('routes.notifications.notificationBell.clearAll'),
    });
    if (ok) await clearAll();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          unreadCount > 0
            ? tx('routes.notifications.notificationBell.notificationsUnreadcountUnread', { unreadCount })
            : tx('routes.notifications.notificationBell.notifications')
        }
        title={tx('routes.notifications.notificationBell.notifications')}
        className="um-icon-btn relative h-9 w-9"
      >
        {BellIcon}
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white shadow-elevation-1"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={tx('routes.notifications.notificationBell.notifications')}
          className="absolute right-0 top-full z-50 mt-1 w-80 max-w-[90vw] overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-3"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">{tx('routes.notifications.notificationBell.notifications')}</span>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => void markAllRead()}
                disabled={unreadCount === 0}
                className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
              >{tx('routes.notifications.notificationBell.markAllAsRead')}</button>
              <button
                type="button"
                onClick={() => void onClearAll()}
                disabled={items.length === 0}
                data-testid="notification-clear-all"
                className="text-xs font-medium text-danger hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
              >{tx('routes.notifications.notificationBell.clearAll')}</button>
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                aria-label={tx('routes.notifications.notificationBell.notificationPreferences')}
                title={tx('routes.notifications.notificationBell.notificationPreferences')}
                className="rounded-token-sm p-1 text-muted transition hover:bg-surface-2 hover:text-foreground"
              >
                {GearIcon}
              </button>
            </div>
          </div>

          <div
            role="group"
            aria-label={tx('routes.notifications.notificationBell.filterNotifications')}
            className="flex items-center gap-1 border-b border-border px-3 py-1.5"
          >
            {(['all', 'ai_staff', 'collaboration', 'publishing'] as GroupFilter[]).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupFilter(g)}
                aria-pressed={groupFilter === g}
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition ${
                  groupFilter === g
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted hover:bg-surface-2 hover:text-foreground'
                }`}
              >
                {GROUP_FILTER_LABEL[g]}
              </button>
            ))}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">{tx('routes.notifications.notificationBell.loading')}</p>
            )}

            {!loading && error && items.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">{tx('routes.notifications.notificationBell.notificationsUnavailableForNow')}</p>
            )}

            {!loading && !error && items.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">{tx('routes.notifications.notificationBell.noNotification')}</p>
            )}

            {!loading && items.length > 0 && filteredItems.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted">{tx('routes.notifications.notificationBell.noNotificationInThisFilter')}</p>
            )}

            {filteredItems.length > 0 && (
              <ul className="divide-y divide-border">
                {filteredItems.map((n) => {
                  const info = kindInfo(n.kind ?? n.type);
                  const standout = !!info.highPriority && !n.read;
                  return (
                    <li key={n.id} className="relative" data-testid={`notification-row-${n.id}`}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          if (!n.read) void markRead(n.id);
                          const to = targetFor(n);
                          setOpen(false);
                          if (to) navigate(to);
                        }}
                        className={`flex w-full items-start gap-2.5 border-l-2 py-2.5 pl-3 pr-9 text-left transition-colors hover:bg-surface-2 ${
                          standout
                            ? 'border-l-warning bg-warning/[0.08]'
                            : `border-l-transparent ${n.read ? '' : 'bg-primary/[0.04]'}`
                        }`}
                      >
                        <span aria-hidden className="mt-0.5 shrink-0 text-base leading-none">
                          {info.icon}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span
                              className={`truncate text-sm ${n.read ? 'text-foreground' : 'font-semibold text-foreground'}`}
                            >
                              {n.title}
                            </span>
                            {standout && (
                              <span className="shrink-0 rounded-full bg-warning/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">{tx('routes.notifications.notificationBell.priority')}</span>
                            )}
                            {!n.read && (
                              <span
                                aria-label={tx('routes.notifications.notificationBell.unread')}
                                className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                              />
                            )}
                          </span>
                          {n.body && (
                            <span className="mt-0.5 block truncate text-xs text-muted">{n.body}</span>
                          )}
                          <span className="mt-0.5 block text-[11px] text-muted">
                            {timeAgo(n.created_at)}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(n.id)}
                        aria-label={tx('routes.notifications.notificationBell.deleteNotification', { title: n.title })}
                        title={tx('routes.notifications.notificationBell.deleteNotificationShort')}
                        data-testid={`notification-delete-${n.id}`}
                        className="absolute right-2 top-2 rounded-token-sm p-1 text-muted transition hover:bg-surface-2 hover:text-danger"
                      >
                        {TrashIcon}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {prefsOpen && <NotificationPrefs onClose={() => setPrefsOpen(false)} />}
    </div>
  );
}
